import type {
  Book,
  BookStatusHistory,
  Goal,
  Note,
  Quote,
  ReadingProgressEntry,
  ReadingSession,
  Series
} from '../domain/entities/types';
import type { BibleBookCode } from '../domain/bibleCanon';

export type UUID = string;
export type Sha256Hex = string;
export type BigIntString = string;

export const SYNC_PROTOCOL_V1 = 1 as const;
export const SYNC_PROTOCOL_V2 = 2 as const;
export const SYNC_TARGET_PROTOCOL_VERSION = SYNC_PROTOCOL_V1;
export const SYNC_PROTOCOL_VERSION = SYNC_TARGET_PROTOCOL_VERSION;
export const SYNC_PAYLOAD_VERSION_V1 = 1 as const;
export const SYNC_PAYLOAD_VERSION_V2 = 2 as const;
export const SYNC_PAYLOAD_VERSION = SYNC_PAYLOAD_VERSION_V1;
export const SYNC_CANONICALIZATION_V1 = 'leitour-sync-json-v1' as const;
export const SYNC_CANONICALIZATION_V2 = 'leitour-sync-json-v2' as const;
export const SYNC_CANONICALIZATION = SYNC_CANONICALIZATION_V1;
export const SYNC_MAX_MUTATIONS_PER_BATCH = 2_000 as const;
export const SYNC_MAX_BATCH_BYTES = 8 * 1024 * 1024;
export const SYNC_MAX_ENTITY_ID_LENGTH = 36 as const;
export const SYNC_MAX_PULL_TARGET_COUNT = 5_000 as const;
export const SYNC_MAX_PULL_BATCHES = 500 as const;

export type SyncProtocolVersion = typeof SYNC_PROTOCOL_V1 | typeof SYNC_PROTOCOL_V2;
export type SyncPayloadVersion = typeof SYNC_PAYLOAD_VERSION_V1 | typeof SYNC_PAYLOAD_VERSION_V2;
export type SyncCanonicalization =
  | typeof SYNC_CANONICALIZATION_V1
  | typeof SYNC_CANONICALIZATION_V2;

export const canonicalizationForProtocol = (
  protocolVersion: SyncProtocolVersion
): SyncCanonicalization => protocolVersion === SYNC_PROTOCOL_V2
  ? SYNC_CANONICALIZATION_V2
  : SYNC_CANONICALIZATION_V1;

export const canonicalizationForPayload = (
  payloadVersion: SyncPayloadVersion
): SyncCanonicalization => payloadVersion === SYNC_PAYLOAD_VERSION_V2
  ? SYNC_CANONICALIZATION_V2
  : SYNC_CANONICALIZATION_V1;

export type SyncEntityType =
  | 'book'
  | 'series'
  | 'note'
  | 'quote'
  | 'goal'
  | 'reading_session'
  | 'reading_progress_entry'
  | 'book_status_history'
  | 'user_preference';

export type SyncOperation = 'create' | 'update' | 'delete' | 'restore';
export type SyncJournalOperation = 'upsert' | 'delete';

type WithoutCover<T> = T extends Book ? Omit<T, 'coverAssetId'> : never;

export type SyncBookPayload = WithoutCover<Book>;
export type SyncCompletedSessionPayload = Extract<ReadingSession, { status: 'completed' }>;
export interface SyncHomeHighlightPreferencePayload {
  id: 'app';
  showHomeHighlight: boolean;
}

export interface SyncBibleBookOrderPreferencePayload {
  id: 'bible_book_order';
  bookOrder: BibleBookCode[];
}

export type SyncUserPreferencePayload =
  | SyncHomeHighlightPreferencePayload
  | SyncBibleBookOrderPreferencePayload;

export interface SyncPayloadMap {
  book: SyncBookPayload;
  series: Series;
  note: Note;
  quote: Quote;
  goal: Goal;
  reading_session: SyncCompletedSessionPayload;
  reading_progress_entry: ReadingProgressEntry;
  book_status_history: BookStatusHistory;
  user_preference: SyncUserPreferencePayload;
}

export type SyncBaseRevision =
  | { state: 'known'; revision: BigIntString }
  | { state: 'unresolved'; reason: 'bootstrap_required' | 'pending_predecessor' };

export interface SyncMutation<E extends SyncEntityType = SyncEntityType> {
  readonly sequence: number;
  readonly entityType: E;
  readonly entityId: string;
  readonly operation: SyncOperation;
  readonly baseRevision: SyncBaseRevision;
  readonly payloadVersion: SyncPayloadVersion;
  readonly payload: SyncPayloadMap[E];
  readonly payloadHash: Sha256Hex;
}

export interface SyncBatch {
  readonly protocolVersion: SyncProtocolVersion;
  readonly canonicalization: SyncCanonicalization;
  readonly batchId: UUID;
  readonly deviceId: UUID;
  readonly createdAt: string;
  readonly mutations: readonly SyncMutation[];
  readonly requestHash: Sha256Hex;
  readonly supersedesBatchId?: UUID;
}

export interface AppliedMutationResult {
  sourceSequence: number;
  entityType: SyncEntityType;
  entityId: string;
  revision: BigIntString;
}

export interface EmittedChangeReference {
  changeId: BigIntString;
  changeSequence: number;
  sourceMutationSequence: number | null;
  entityType: SyncEntityType;
  entityId: string;
  operation: SyncJournalOperation;
  revision: BigIntString;
  payloadHash: Sha256Hex;
}

export interface SyncServerConflict {
  sourceSequence: number;
  entityType: SyncEntityType;
  entityId: string;
  reason: SyncConflictReason;
  remoteRevision?: BigIntString;
  remotePayload?: unknown;
  remotePayloadHash?: Sha256Hex;
  remoteDeletedAt?: string;
  dependencyEntityType?: SyncEntityType;
  dependencyEntityId?: string;
  dependentEntityType?: SyncEntityType;
  dependentEntityId?: string;
  conflictingEntityId?: string;
}

export type SyncBatchResult =
  | {
      status: 'applied';
      replayed: boolean;
      batchId: UUID;
      requestHash: Sha256Hex;
      committedAt: string;
      firstChangeId: BigIntString;
      lastChangeId: BigIntString;
      mutations: AppliedMutationResult[];
      emittedChanges: EmittedChangeReference[];
    }
  | { status: 'conflict'; batchId: UUID; conflicts: SyncServerConflict[] }
  | { status: 'rejected'; batchId: UUID; code: string; message: string; retryable: false };

export interface SyncChange<E extends SyncEntityType = SyncEntityType> {
  changeId: BigIntString;
  batchId: UUID;
  changeSequence: number;
  sourceMutationSequence: number | null;
  entityType: E;
  entityId: string;
  operation: SyncJournalOperation;
  revision: BigIntString;
  payloadVersion: SyncPayloadVersion;
  payload: SyncPayloadMap[E];
  payloadHash: Sha256Hex;
  changedAt: string;
}

export interface SyncSnapshotEntity<E extends SyncEntityType = SyncEntityType> {
  entityType: E;
  entityId: string;
  operation: SyncJournalOperation;
  revision: BigIntString;
  payloadVersion: SyncPayloadVersion;
  payload: SyncPayloadMap[E];
  payloadHash: Sha256Hex;
  deletedAt?: string;
}

export interface SyncSnapshot {
  protocolVersion?: SyncProtocolVersion;
  canonicalization?: SyncCanonicalization;
  highWaterChangeId: BigIntString;
  entities: SyncSnapshotEntity[];
}

export interface SyncPullResult {
  protocolVersion?: SyncProtocolVersion;
  canonicalization?: SyncCanonicalization;
  changes: SyncChange[];
  nextCursor: BigIntString;
  hasMore: boolean;
}

export type SyncChangeIntegrityVerification<E extends SyncEntityType = SyncEntityType> =
  | {
      status: 'verified';
      change: SyncChange<E>;
      calculatedPayloadHash: Sha256Hex;
    }
  | {
      status: 'invalid';
      code: 'payload_hash_mismatch';
      retryable: false;
      declaredPayloadHash: Sha256Hex;
      calculatedPayloadHash: Sha256Hex;
      mustRejectChange: true;
      mustRecordIntegrityError: true;
      mustPreserveCursor: true;
      mustPreserveLocalData: true;
    };

export interface SyncCursor {
  lastChangeId: BigIntString;
}

export interface SyncEntityState {
  entityType: SyncEntityType;
  entityId: string;
  remoteRevision: BigIntString;
  remotePayloadHash: Sha256Hex;
  lastChangeId: BigIntString;
  deletedAt?: string | undefined;
}

export type SyncConflictReason =
  | 'revision_mismatch'
  | 'bootstrap_divergence'
  | 'remote_deleted'
  | 'uuid_collision'
  | 'dependency_conflict'
  | 'series_name_collision'
  | 'goal_year_collision'
  | 'payload_hash_mismatch'
  | 'invalid_local_state'
  | 'batch_conflict';

export interface SyncConflict {
  id: UUID;
  status: 'open' | 'resolved';
  entityType: SyncEntityType;
  entityId: string;
  reason: SyncConflictReason;
  origin?: 'bootstrap' | 'pull' | 'push' | undefined;
  batchId?: UUID | undefined;
  mutationSequence?: number | undefined;
  baseRevision: SyncBaseRevision;
  localPayload?: unknown;
  localPayloadHash?: Sha256Hex | undefined;
  remoteRevision?: BigIntString | undefined;
  remotePayload?: unknown;
  remotePayloadHash?: Sha256Hex | undefined;
  remotePayloadVersion?: SyncPayloadVersion | undefined;
  remoteDeletedAt?: string | undefined;
  dependencyEntityType?: SyncEntityType | undefined;
  dependencyEntityId?: string | undefined;
  dependentEntityType?: SyncEntityType | undefined;
  dependentEntityId?: string | undefined;
  conflictingEntityId?: string | undefined;
  detectedAt: string;
  resolvedAt?: string | undefined;
  resolution?: 'accept_remote' | 'rebase_local' | 'duplicate_local' | 'merge' | undefined;
}

export type SyncBootstrapState = 'not_started' | 'running' | 'complete' | 'conflicted' | 'failed';

export type SyncProtocolUpgradeStage =
  | 'resolve_sending_v1'
  | 'snapshot_v2'
  | 'pull_v2'
  | 'rebase_outbox'
  | 'finalize'
  | 'complete';

export interface SyncProtocolUpgradeState {
  status: 'running' | 'complete';
  fromProtocolVersion: typeof SYNC_PROTOCOL_V1;
  targetProtocolVersion: typeof SYNC_PROTOCOL_V2;
  stage: SyncProtocolUpgradeStage;
  startedAt: string;
  snapshotHighWaterChangeId?: BigIntString | undefined;
  lastStableCursor?: BigIntString | undefined;
  completedAt?: string | undefined;
}

export interface RemoteSyncRolloutAuthorization {
  status: 'not_checked' | 'allowed' | 'denied';
  checkedAt?: string;
  cohort?: string;
}

export interface SyncMetadata {
  id: 'sync';
  protocolVersion: SyncProtocolVersion;
  canonicalization: SyncCanonicalization;
  userId: UUID;
  deviceId: UUID;
  cursor: SyncCursor;
  bootstrapState: SyncBootstrapState;
  remoteRollout: RemoteSyncRolloutAuthorization;
  bootstrapHighWaterChangeId?: BigIntString | undefined;
  lastAttemptAt?: string | undefined;
  lastSuccessfulSyncAt?: string | undefined;
  lastError?: string | undefined;
  consecutiveFailures?: number | undefined;
  protocolUpgrade?: SyncProtocolUpgradeState | undefined;
}

export type SyncOutboxStatus = 'awaiting_bootstrap' | 'pending' | 'sending' | 'conflicted' | 'rejected' | 'superseded';

export interface SyncOutboxRecord {
  batchId: UUID;
  userId: UUID;
  queueOrder: number;
  status: SyncOutboxStatus;
  batch: SyncBatch;
  attemptCount: number;
  createdAt: string;
  nextAttemptAt: string;
  lastAttemptAt?: string | undefined;
  lastError?: string | undefined;
}

export interface SyncMutationDraft<E extends SyncEntityType = SyncEntityType> {
  entityType: E;
  entityId: string;
  operation: SyncOperation;
  payload: SyncPayloadMap[E];
}

export interface SyncCoverState {
  coverId: UUID;
  bookId: UUID;
  sha256: Sha256Hex;
  storagePath: string;
  mimeType: string;
  byteSize: number;
  status: 'pending_upload' | 'uploaded' | 'cached' | 'orphaned';
  remoteDeletedAt?: string | undefined;
  updatedAt: string;
}

export interface SyncRemoteCoverMetadata {
  coverId: UUID;
  bookId: UUID;
  sha256: Sha256Hex;
  storagePath: string;
  mimeType: string;
  byteSize: number;
  width: number;
  height: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | undefined;
}

export interface SyncCoverConflict {
  id: UUID;
  bookId: UUID;
  status: 'open' | 'resolved';
  reason: 'replacement_conflict';
  localCoverId?: UUID | undefined;
  remoteCoverId?: UUID | undefined;
  remoteMetadata?: SyncRemoteCoverMetadata | undefined;
  detectedAt: string;
  resolvedAt?: string | undefined;
  resolution?: 'accept_remote' | 'keep_local' | undefined;
}

export type SyncActivity = 'disabled' | 'offline' | 'idle' | 'pending' | 'bootstrapping' | 'syncing' | 'conflict' | 'error';

export interface SyncRuntimeStatus {
  activity: SyncActivity;
  effectiveEnabled: boolean;
  remoteRolloutStatus: RemoteSyncRolloutAuthorization['status'];
  bootstrapState: SyncBootstrapState;
  lastSuccessfulSyncAt?: string | undefined;
  errorCode?: string | undefined;
  openConflictCount: number;
  canResetTechnicalState: boolean;
}

export interface SyncTelemetryEvent {
  type: 'bootstrap' | 'pull' | 'push' | 'cover' | 'restore';
  outcome: 'started' | 'success' | 'retry' | 'conflict' | 'error';
  durationMs?: number;
  batchCount?: number;
  mutationCount?: number;
  bootstrapState?: SyncBootstrapState;
  retryCount?: number;
  errorCode?: string;
}
